import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { useTranslation } from '@ibk/shared-translation';
import { OperatorRoles, SYS_ADMIN } from '@ibk/shared-types';
import { Button, Icon, Loading } from '@ibk/shared-ui';
import { SearchBar } from '../../../../components/SearchBar';
import { EmptyOperatorsList } from '../EmptyOperatorsList/EmptyOperatorsList';
import styles from './OperatorsList.module.css';
function isOperatorRole(value) {
    return Object.values(OperatorRoles).includes(value);
}
export function getRemovableOperatorRoleId(operator) {
    const bySlug = (slug) => operator.roles.find(role => role.slug === slug);
    const operatorRole = bySlug(OperatorRoles.OPERATOR);
    if (operatorRole)
        return operatorRole.id;
    const managingRole = bySlug(OperatorRoles.MANAGING_PARTNER);
    if (managingRole)
        return managingRole.id;
    const nonAdmin = operator.roles.find(role => role.slug !== SYS_ADMIN);
    if (nonAdmin)
        return nonAdmin.id;
    return operator.roles[0]?.id;
}
const OperatorRolesTranslation = {
    [OperatorRoles.OPERATOR]: 'operator.drawer.pages.register-form.card.operator',
    [OperatorRoles.MANAGING_PARTNER]: 'operator.drawer.pages.register-form.card.managing_partner',
};
export const OperatorsList = ({ operatorsList, onDeleteOperator, isLoading, isRefetching, goToRegister, }) => {
    const { t } = useTranslation();
    const [filterText, setFilterText] = React.useState('');
    const capitalizeFirst = (value) => {
        if (!value)
            return '';
        const trimmed = value.trim();
        if (trimmed.length === 0)
            return '';
        return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
    };
    const filteredOperators = React.useMemo(() => {
        const normalizedFilter = filterText.trim().toLowerCase();
        return [...operatorsList]
            .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`))
            .filter(operator => `${operator.firstName} ${operator.lastName}`.toLowerCase().includes(normalizedFilter));
    }, [operatorsList, filterText]);
    const isBusy = Boolean(isLoading) || Boolean(isRefetching);
    if (isBusy) {
        return (_jsx("div", { className: `${styles.container} ${styles.loadingContainer}`, children: _jsx(Loading, {}) }));
    }
    if (operatorsList.length === 0) {
        return (_jsxs("div", { className: styles.container, children: [_jsx("div", { className: styles.searchBar, children: _jsx(SearchBar, { value: filterText, onChange: setFilterText, placeholder: t('operator.drawer.pages.home.search-operator') }) }), _jsx("ul", { className: styles.listContainer, children: _jsx(EmptyOperatorsList, { mode: 'noOperators', buttonClick: () => goToRegister() }) })] }));
    }
    const noResults = filterText.trim() !== '' && filteredOperators.length === 0;
    function getOperatorRoleTranslation(role) {
        if (isOperatorRole(role)) {
            return OperatorRolesTranslation[role];
        }
        if (role === SYS_ADMIN) {
            return 'operator.drawer.pages.register-form.card.admin';
        }
        return 'operator.drawer.pages.register-form.card.no-profile';
    }
    const hasAdminRole = (operator) => {
        return operator.roles.some(role => role.slug === SYS_ADMIN);
    };
    const getRolesDisplay = (operator) => {
        if (!operator.roles.length) {
            return t(getOperatorRoleTranslation(''));
        }
        return operator.roles.map(role => t(getOperatorRoleTranslation(role.slug))).join(', ');
    };
    return (_jsxs("div", { className: styles.container, children: [_jsx("div", { className: styles.searchBar, children: _jsx(SearchBar, { value: filterText, onChange: setFilterText, placeholder: t('operator.drawer.pages.home.search-operator') }) }), _jsx("ul", { className: styles.listContainer, children: noResults ? (_jsx(EmptyOperatorsList, { mode: 'noResults', buttonClick: () => goToRegister() })) : (filteredOperators.map(operator => {
                    const fullName = `${capitalizeFirst(operator.firstName)} ${capitalizeFirst(operator.lastName)}`.trim();
                    const isAdminUser = hasAdminRole(operator);
                    const canDelete = !isAdminUser && onDeleteOperator;
                    return (_jsxs("li", { className: styles.operatorItem, children: [_jsxs("div", { className: styles.operatorInfo, children: [_jsx("p", { className: styles.operatorName, children: fullName || t('operator.drawer.pages.register-form.card.no-name') }), _jsx("p", { className: styles.operatorRoles, children: getRolesDisplay(operator) })] }), canDelete && (_jsx("button", { type: 'button', "aria-label": t('operator.drawer.pages.home.remove-operator'), onClick: e => {
                                    e.stopPropagation();
                                    onDeleteOperator({
                                        partyId: operator.partyId,
                                        roleId: getRemovableOperatorRoleId(operator),
                                    });
                                }, className: styles.deleteOperatorIcon, children: _jsx(Icon, { name: 'actionTrash', className: styles.deleteOperatorIcon }) }))] }, operator.personId));
                })) }), _jsx("div", { className: styles.footer, children: _jsx(Button, { variant: 'primary', size: 'medium', dataTestId: 'operator.drawer.pages.home.add-operator', onClick: () => goToRegister(), className: styles.footerButton, children: t(`operator.drawer.pages.home.add-operator`) }) })] }));
};
